import { createStore, del as idbDel, get as idbGet, keys as idbKeys, set as idbSet } from "idb-keyval";
import type { PersistedEnvelope } from "../types";
import type { StorageBackend } from "./StorageBackend";

/** Dedicated IndexedDB database/object store for large slice payloads (e.g. search history). */
const idbStore = createStore( "flowride_persistence", "slices" );

/**
 * IndexedDB backend via `idb-keyval` for slices that may grow beyond Preferences limits.
 */
export const idbKeyvalBackend: StorageBackend = {
    "kind": "idb",

    /**
     * Reads an envelope stored as a structured object (no JSON round-trip).
     *
     * @typeParam T Expected `data` type.
     * @param key Slice key (used as idb-keyval key directly).
     */
    async get<T>( key: string ): Promise<PersistedEnvelope<T> | null> {
        const value = await idbGet<PersistedEnvelope<T>>( key, idbStore );

        return value ?? null;
    },

    /**
     * Writes an envelope object into IndexedDB.
     *
     * @typeParam T Envelope `data` type.
     * @param key Slice key.
     * @param envelope Versioned blob.
     */
    async set<T>( key: string, envelope: PersistedEnvelope<T> ): Promise<void> {
        await idbSet( key, envelope, idbStore );
    },

    /**
     * Deletes a slice entry from IndexedDB.
     *
     * @param key Slice key.
     */
    async del( key: string ): Promise<void> {
        await idbDel( key, idbStore );
    },

    /**
     * Returns all slice keys present in the persistence object store.
     */
    async keys(): Promise<string[]> {
        return await idbKeys( idbStore );
    },
};
